import { useState, type FormEvent } from 'react';
import {
  useTaskTemplates,
  useTaskTemplate,
  useCreateTaskTemplate,
  useDeleteTaskTemplate,
  useAddTemplateItem,
  useUpdateTemplateItem,
  useDeleteTemplateItem,
} from '../api/queries/task-templates';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { LookupSelect } from '../components/LookupSelect';
import { useLookupLabel } from '../api/queries/lookups';
import type { CaseType } from '@stfors/shared-types';
import './TemplatesPage.css';
import { Toggle } from '../components/Toggle';

interface NewItemDraft {
  text: string;
  is_required: boolean;
}

export function TemplatesPage(): JSX.Element {
  const { data, isPending, error } = useTaskTemplates();
  const createTemplate = useCreateTaskTemplate();
  const deleteTemplate = useDeleteTaskTemplate();
  const caseTypeLabel = useLookupLabel('case_type');

  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCaseType, setNewCaseType] = useState<CaseType | ''>('');
  const [newItems, setNewItems] = useState<NewItemDraft[]>([{ text: '', is_required: false }]);
  const [createError, setCreateError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  const onAddItem = () => {
    setNewItems([...newItems, { text: '', is_required: false }]);
  };

  const onRemoveItem = (idx: number) => {
    setNewItems(newItems.filter((_, i) => i !== idx));
  };

  const onUpdateItem = (idx: number, patch: Partial<NewItemDraft>) => {
    setNewItems(newItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newName.trim().length === 0) return;

    const cleanedItems = newItems
      .map((it) => ({ text: it.text.trim(), is_required: it.is_required }))
      .filter((it) => it.text.length > 0);

    try {
      await createTemplate.mutateAsync({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        case_type: newCaseType === '' ? undefined : newCaseType,
        items: cleanedItems.length > 0 ? cleanedItems : undefined,
      });
      setShowNew(false);
      setNewName('');
      setNewDescription('');
      setNewCaseType('');
      setNewItems([{ text: '', is_required: false }]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to create templates.");
        return;
      }
      setCreateError('Could not save template. Try again.');
    }
  };

  if (isPending) return <p className="tp-status">Loading templates…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view templates."
        : 'Failed to load templates.';
    return <p className="tp-status tp-error">{msg}</p>;
  }

  const templates = data?.task_templates ?? [];

  return (
    <section className="page-stack page-stack-wide">
      <div className="card tp-toolbar">
      <header className="tp-header">
        <h2>Task Templates</h2>
        <div className="tp-header-controls">
          {can('project.write') && !showNew && (
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => setShowNew(true)}
            >
              New template
            </button>
          )}
        </div>
      </header>
      </div>

      {showNew && (
        <div className="card">
        <form className="tp-new-form" onSubmit={onCreate}>
          <div className="tp-new-row">
            <label className="tp-field tp-field-wide">
              <span>Name *</span>
              <input
                type="text"
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Criminal defense intake checklist"
                maxLength={200}
                required
                disabled={createTemplate.isPending}
              />
            </label>
            <label className="tp-field">
              <span>Case type</span>
              <LookupSelect
                category="case_type"
                value={newCaseType}
                onChange={(v) => setNewCaseType(v as CaseType | '')}
                className="form-input"
                disabled={createTemplate.isPending}
                allowEmpty
                emptyLabel="All types"
              />
            </label>
          </div>
          <label className="tp-field tp-field-wide">
            <span>Description</span>
            <textarea
              className="form-input tp-textarea"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={2}
              maxLength={10_000}
              disabled={createTemplate.isPending}
            />
          </label>

          <div className="tp-items-section">
            <div className="tp-items-header">
              <span>Checklist items</span>
              <button
                type="button"
                className="tp-link"
                onClick={onAddItem}
                disabled={createTemplate.isPending}
              >
                + add item
              </button>
            </div>
            {newItems.map((item, idx) => (
              <div key={idx} className="tp-item-row">
                <span className="tp-item-pos">{idx + 1}.</span>
                <input
                  type="text"
                  className="form-input tp-item-text"
                  value={item.text}
                  onChange={(e) => onUpdateItem(idx, { text: e.target.value })}
                  placeholder="What needs to happen?"
                  maxLength={500}
                  disabled={createTemplate.isPending}
                />
                <Toggle
                  checked={item.is_required}
                  onChange={(v) => onUpdateItem(idx, { is_required: v })}
                  label="Required"
                  disabled={createTemplate.isPending}
                />
                {newItems.length > 1 && (
                  <button
                    type="button"
                    className="tp-item-remove"
                    onClick={() => onRemoveItem(idx)}
                    disabled={createTemplate.isPending}
                    aria-label="Remove item"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {createError && <p className="tp-error-banner" role="alert">{createError}</p>}
          <div className="tp-new-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                setCreateError(null);
              }}
              disabled={createTemplate.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={createTemplate.isPending || newName.trim().length === 0}
            >
              {createTemplate.isPending ? 'Saving…' : 'Create template'}
            </button>
          </div>
        </form>
        </div>
      )}

      {templates.length === 0 ? (
        <div className="card">
          <div className="tp-empty">
            <p>No templates yet.</p>
            {can('project.write') && (
              <p className="tp-empty-meta">
                Templates are reusable checklists you can apply to a project to seed its tasks.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-tight tp-list-card">
        <ul className="tp-list">
          {templates.map((t) => (
            <li key={t.id} className="tp-row">
              <div
                className="tp-row-summary"
                onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(expandedId === t.id ? null : t.id);
                  }
                }}
              >
                <div className="tp-row-main">
                  <div className="tp-row-name">{t.name}</div>
                  {t.description && (
                    <div className="tp-row-desc">{t.description}</div>
                  )}
                </div>
                <div className="tp-row-meta">
                  {t.case_type && (
                    <span className="tp-chip tp-chip-cyan">
                      {caseTypeLabel(t.case_type)}
                    </span>
                  )}
                  <span className="tp-row-count">
                    {t.item_count ?? 0} item{t.item_count === 1 ? '' : 's'}
                  </span>
                  <span className="tp-row-arrow">{expandedId === t.id ? '▾' : '▸'}</span>
                </div>
              </div>
              {expandedId === t.id && (
                <ExpandedTemplate
                  templateId={t.id}
                  onDelete={() => {
                    if (confirm(`Delete template "${t.name}"?`)) {
                      deleteTemplate.mutate(t.id);
                      setExpandedId(null);
                    }
                  }}
                  deleting={deleteTemplate.isPending}
                />
              )}
            </li>
          ))}
        </ul>
        </div>
      )}
    </section>
  );
}

interface ExpandedTemplateProps {
  templateId: number;
  onDelete: () => void;
  deleting: boolean;
}

function ExpandedTemplate({ templateId, onDelete, deleting }: ExpandedTemplateProps): JSX.Element {
  const { data, isPending, error } = useTaskTemplate(templateId);
  const addItem = useAddTemplateItem();
  const updateItem = useUpdateTemplateItem();
  const deleteItem = useDeleteTemplateItem();

  const [newText, setNewText] = useState('');
  const [newRequired, setNewRequired] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  if (isPending) return <p className="tp-expand-status">Loading…</p>;
  if (error) return <p className="tp-expand-status tp-error">Could not load items.</p>;

  const items = data?.task_template.items ?? [];
  const canEdit = can('project.write');

  const onAddItem = () => {
    const trimmed = newText.trim();
    if (trimmed.length === 0) return;
    addItem.mutate(
      { template_id: templateId, text: trimmed, is_required: newRequired },
      {
        onSuccess: () => {
          setNewText('');
          setNewRequired(false);
        },
      },
    );
  };

  const startItemEdit = (id: number, text: string) => {
    setEditingItemId(id);
    setEditText(text);
  };

  const saveItemEdit = (item_id: number) => {
    updateItem.mutate(
      { template_id: templateId, item_id, text: editText.trim() },
      { onSuccess: () => setEditingItemId(null) },
    );
  };

  return (
    <div className="tp-expand">
      {items.length === 0 ? (
        <p className="tp-expand-empty">No items in this template.</p>
      ) : (
        <ol className="tp-expand-list">
          {items.map((it) => {
            const isEditing = editingItemId === it.id;
            return (
              <li key={it.id} className="tp-expand-item">
                {isEditing ? (
                  <input
                    type="text"
                    className="form-input tp-expand-edit"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    disabled={updateItem.isPending}
                  />
                ) : (
                  <span className="tp-expand-text">{it.text}</span>
                )}
                {it.is_required && !isEditing && (
                  <span className="tp-chip tp-chip-warn">Required</span>
                )}
                {canEdit && (
                  <span className="tp-expand-item-actions">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="tp-link"
                          onClick={() => saveItemEdit(it.id)}
                          disabled={updateItem.isPending}
                        >
                          save
                        </button>
                        <button
                          type="button"
                          className="tp-link"
                          onClick={() => setEditingItemId(null)}
                          disabled={updateItem.isPending}
                        >
                          cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="tp-link"
                          onClick={() => startItemEdit(it.id, it.text)}
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className="tp-link tp-danger"
                          onClick={() => {
                            if (confirm('Remove this item?')) {
                              deleteItem.mutate({ template_id: templateId, item_id: it.id });
                            }
                          }}
                          disabled={deleteItem.isPending}
                        >
                          delete
                        </button>
                      </>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {canEdit && (
        <div className="tp-expand-add">
          <input
            type="text"
            className="form-input tp-expand-add-input"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Add an item to this template…"
            maxLength={500}
            disabled={addItem.isPending}
          />
          <Toggle
            checked={newRequired}
            onChange={setNewRequired}
            label="Required"
            disabled={addItem.isPending}
          />
          <button
            type="button"
            className="btn-cyan-primary tp-expand-add-btn"
            onClick={onAddItem}
            disabled={addItem.isPending || newText.trim().length === 0}
          >
            {addItem.isPending ? 'Adding…' : 'Add item'}
          </button>
        </div>
      )}
      {canEdit && (
        <div className="tp-expand-actions">
          <button
            type="button"
            className="tp-link tp-danger"
            onClick={onDelete}
            disabled={deleting}
          >
            Delete template
          </button>
        </div>
      )}
    </div>
  );
}
